/**
 * A member's own account: the balance, the statements, the cards on
 * file, and what the club is allowed to send them.
 *
 * Every endpoint on this page already existed. `assertCanAccessAccount`
 * has scoped `/v1/member-accounts/:id` and its statements and payment
 * methods to the caller's own accounts since the beginning, and
 * `/v1/me/contact-preferences` has been there since 0112. What did not
 * exist was any way for a member to REACH them: the only screens over
 * these routes are the ones the desk opens, all of which start by
 * listing every member in the club.
 *
 * So this is not new capability. It is the door onto capability that
 * has been sitting behind a staff-only screen, which is the whole
 * finding: a member-facing API with no member-facing page is the same
 * thing as no API at all.
 *
 * THE ACCOUNT COMES FROM THE TOKEN, never from the URL. The routes take
 * an id, and a screen that read one out of the address bar would be a
 * screen inviting somebody to try the next uuid — the API would refuse
 * it, and the refusal would be the first thing the software ever said
 * to them. `member_account_ids` is on the principal; a household with
 * two accounts gets a picker rather than a guess.
 *
 * WHAT IS NOT HERE, and deliberately:
 *
 *   No card number field. `payment_method` stores a processor token,
 *   four digits and an expiry, and a number never enters the database,
 *   a log, or the process — the form takes the reference the terminal
 *   or the processor hands back. A box on this page that accepted
 *   sixteen digits would undo the one decision that keeps this product
 *   out of PCI scope, so it does not exist and the placeholder says so.
 *
 *   No 'emergency' row in the preferences table that can be switched
 *   off. `fn_member_set_consent` refuses it in the database, which is
 *   where that refusal belongs; the screen shows it as fixed rather
 *   than offering a toggle that raises an error when pressed.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Money, Status } from '../components/ui.tsx';
import { date as fmtDate } from '../lib/format.ts';

/* Shapes — mirroring the API's JSON one for one. */

interface Account {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
  balance_minor: number;
  bucket_current_minor: number;
  bucket_30_minor: number;
  bucket_60_minor: number;
  bucket_90_plus_minor: number;
  currency: string;
  autopay_enabled: boolean;
  minimum: {
    period_starts_on: string;
    period_ends_on: string;
    required_minor: number;
    qualifying_spend_minor: number;
    remaining_minor: number;
  } | null;
}

interface Statement {
  id: string;
  statement_number: string;
  period_starts_on: string;
  period_ends_on: string;
  issued_on: string | null;
  due_on: string | null;
  closing_balance_minor: number;
  status: string;
  pdf_uri: string | null;
}

interface PaymentMethod {
  id: string;
  method_type: string;
  display_brand: string | null;
  display_last4: string | null;
  expires_month: number | null;
  expires_year: number | null;
  is_default: boolean;
  is_autopay: boolean;
  status: string;
}

interface Preference {
  topic: string;
  channel: string;
  allowed: boolean;
  is_locked?: boolean;
}

interface Preferences {
  preferences: Preference[];
  channels: { sms: boolean; email: boolean };
  contact: { phone: string | null; email: string | null };
}

const TOPIC_SAYS: Record<string, string> = {
  statement: 'My statement',
  billing: 'Bills and payment reminders',
  events: 'What is on at the club',
  newsletter: 'The newsletter',
  marketing: 'Offers from the club',
  emergency: 'Closures and emergencies',
};

export function MyAccountPage() {
  const { me, currency } = useSession();
  const accounts = me?.principal.member_account_ids ?? [];

  const [chosen, setChosen] = useState<string>('');
  const id = chosen || accounts[0] || '';

  const account = useApi<Account>(id ? `/v1/member-accounts/${id}` : null, [id]);
  const statements = useApi<{ statements: Statement[] }>(
    id ? `/v1/member-accounts/${id}/statements` : null, [id]);
  const methods = useApi<{ payment_methods: PaymentMethod[] }>(
    id ? `/v1/member-accounts/${id}/payment-methods` : null, [id]);
  const prefs = useApi<Preferences>('/v1/me/contact-preferences');

  if (accounts.length === 0) {
    return (
      <Card title="My Account">
        <Empty>
          This login is not attached to a member account. If that is wrong, the
          office can join the two.
        </Empty>
      </Card>
    );
  }

  return (
    <>
      {/* A household with two accounts — a member and a corporate one,
          a parent paying for a junior — picks. One account gets no
          control at all rather than a dropdown with a single option. */}
      {accounts.length > 1 && (
        <Card title="Which Account">
          <select value={id} onChange={(e) => setChosen(e.target.value)}>
            {accounts.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </Card>
      )}

      {/* ============================================================= */}
      <Card title="What I Owe">
        <ErrorBanner error={account.error} />
        {account.loading ? (
          <Loading rows={2} />
        ) : !account.data ? (
          <Empty>Nothing to show.</Empty>
        ) : (
          <>
            <div className="row row--wrap" style={{ gap: 24, alignItems: 'baseline' }}>
              <div>
                <div className="muted small">Balance</div>
                <h2 style={{ margin: 0 }}>
                  <Money minor={account.data.balance_minor} currency={currency} />
                </h2>
              </div>
              <div>
                <div className="muted small">Account</div>
                <div className="mono">{account.data.account_number}</div>
              </div>
              <div>
                <div className="muted small">Standing</div>
                {account.data.status === 'active'
                  ? <Status tone="good">Good standing</Status>
                  : <Status tone="warning">{account.data.status.replace('_', ' ')}</Status>}
              </div>
              <div>
                <div className="muted small">Autopay</div>
                {account.data.autopay_enabled
                  ? <Status tone="good">On</Status>
                  : <Status tone="neutral">Off</Status>}
              </div>
            </div>

            {/* The minimum, when there is one. A member finds out about
                an unmet minimum on the statement that charges them for
                it, which is the worst possible moment and the reason
                this is on the front of their own page. */}
            {account.data.minimum && (
              <p className="muted small" style={{ marginTop: 16 }}>
                {account.data.minimum.remaining_minor > 0 ? (
                  <>
                    <Money minor={account.data.minimum.remaining_minor} currency={currency} />
                    {' '}left to spend before {fmtDate(account.data.minimum.period_ends_on)} to
                    meet this period&rsquo;s minimum.
                  </>
                ) : (
                  <>This period&rsquo;s minimum is met.</>
                )}
              </p>
            )}
          </>
        )}
      </Card>

      {/* ============================================================= */}
      <Card title="My Statements" flush>
        <ErrorBanner error={statements.error} />
        {statements.loading ? (
          <Loading rows={3} />
        ) : (statements.data?.statements ?? []).length === 0 ? (
          <Empty>No statements have been issued yet.</Empty>
        ) : (
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Period</th>
                <th style={{ width: 130 }}>Issued</th>
                <th style={{ width: 130 }}>Due</th>
                <th style={{ width: 140 }}>Closing</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {(statements.data?.statements ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    {fmtDate(s.period_starts_on)} – {fmtDate(s.period_ends_on)}
                    <div className="muted small mono">{s.statement_number}</div>
                  </td>
                  <td className="mono">{s.issued_on ? fmtDate(s.issued_on) : '—'}</td>
                  <td className="mono">{s.due_on ? fmtDate(s.due_on) : '—'}</td>
                  <td>
                    <Money minor={s.closing_balance_minor} currency={currency} />
                  </td>
                  <td>
                    {/* Only when there is one. A link to a PDF the club
                        has not rendered is a 404 with the club's name
                        on it. */}
                    {s.pdf_uri
                      ? <a href={s.pdf_uri} target="_blank" rel="noreferrer">Open</a>
                      : <span className="muted small">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ============================================================= */}
      <Card title="Cards on File" flush>
        <ErrorBanner error={methods.error} />
        {methods.loading ? (
          <Loading rows={2} />
        ) : (methods.data?.payment_methods ?? []).length === 0 ? (
          <Empty>
            Nothing on file. The office can add a card, and it is the terminal&rsquo;s
            reference that is kept — never the number.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Card</th>
                <th style={{ width: 120 }}>Expires</th>
                <th style={{ width: 220 }}>Used for</th>
              </tr>
            </thead>
            <tbody>
              {(methods.data?.payment_methods ?? []).map((m) => (
                <tr key={m.id}>
                  <td>
                    {m.display_brand ?? m.method_type}
                    {m.display_last4 ? <span className="mono"> ···· {m.display_last4}</span> : null}
                  </td>
                  <td className="mono">
                    {m.expires_month && m.expires_year
                      ? `${String(m.expires_month).padStart(2, '0')}/${m.expires_year}`
                      : '—'}
                  </td>
                  <td>
                    {m.is_default && <Status tone="good">Default</Status>}{' '}
                    {m.is_autopay && <Status tone="good">Autopay</Status>}
                    {!m.is_default && !m.is_autopay && (
                      <span className="muted small">On file</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ============================================================= */}
      <ContactPreferences prefs={prefs} />
    </>
  );
}

/**
 * What the club may send, and down which channel.
 *
 * Three separate facts have to be on this screen or a toggle lies:
 * whether the MEMBER allows it, whether the CLUB has that channel
 * switched on at all, and whether the club has anywhere to send —
 * turning on texts with no number on file changes nothing, and the
 * member has no way to discover that from a switch that went green.
 */
function ContactPreferences({ prefs }: { prefs: ReturnType<typeof useApi<Preferences>> }) {
  const set = useMutation(async (topic: string, channel: string, allowed: boolean) =>
    api('/v1/me/contact-preferences', {
      method: 'PUT',
      body: { topic, channel, allowed },
    }));

  const rows = prefs.data?.preferences ?? [];
  const channels = prefs.data?.channels;
  const contact = prefs.data?.contact;

  // The API returns one row per topic per channel; the screen is one row
  // per topic with a box for each channel, which is how somebody thinks
  // about it.
  const topics = [...new Set(rows.map((r) => r.topic))];
  const allowed = (topic: string, channel: string) =>
    rows.find((r) => r.topic === topic && r.channel === channel)?.allowed ?? false;

  return (
    <Card title="How the Club Reaches Me" flush>
      <ErrorBanner error={prefs.error ?? set.error} />

      {contact && (!contact.email || !contact.phone) && (
        <p className="muted small" style={{ padding: '12px 16px 0' }}>
          {!contact.email && !contact.phone
            ? 'The club has neither an email address nor a telephone number for you, so nothing switched on here will arrive. The office can add them.'
            : !contact.email
              ? 'The club has no email address for you, so nothing switched on in that column will arrive.'
              : 'The club has no telephone number for you, so nothing switched on in that column will arrive.'}
        </p>
      )}

      {prefs.loading ? (
        <Loading rows={4} />
      ) : topics.length === 0 ? (
        <Empty>This club has not set up notifications yet.</Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>About</th>
              <th style={{ width: 120 }}>Email</th>
              <th style={{ width: 120 }}>Text</th>
            </tr>
          </thead>
          <tbody>
            {topics.map((topic) => (
              <tr key={topic}>
                <td>{TOPIC_SAYS[topic] ?? topic}</td>
                {(['email', 'sms'] as const).map((channel) => {
                  // Emergency is not a choice, and the database is where
                  // that refusal lives (`fn_member_set_consent`). Showing
                  // it as fixed is honest; showing a switch that raises
                  // an error when pressed is not.
                  const locked = topic === 'emergency';
                  const off = !channels?.[channel === 'sms' ? 'sms' : 'email'];
                  return (
                    <td key={channel}>
                      {locked ? (
                        <span className="muted small">Always</span>
                      ) : off ? (
                        <span className="muted small">Not used here</span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={allowed(topic, channel)}
                          disabled={set.busy}
                          onChange={async (e) => {
                            if (await set.run(topic, channel, e.target.checked)) {
                              prefs.reload();
                            }
                          }}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
